// Hand-written, per-category SEO copy for /best/{cat}-discord-servers pages.
// The whole point is uniqueness — Google penalises the templated "best X" copy
// that used to live in BestList.jsx as near-duplicate. Every category here has
// its own intro, its own "what to look for", and 5 unique FAQ items whose
// wording does NOT overlap with the other categories.

export const CATEGORY_META = {
  gaming: {
    label: "Gaming Discord Servers",
    noun: "gaming",
    intro:
      "Gaming Discord servers are where every serious player ends up eventually — whether you're grinding ranked, hunting Fortnite squads, coordinating Valorant scrims, running a Minecraft SMP, or chasing Elden Ring co-op partners. This ranked list surfaces the top 25 gaming communities on Serverlistr right now, weighted by live Discord member counts and how active the server has been over the past 48 hours. Every server here is bot-verified — no dead invites, no fake counts. You'll find broad multi-game hubs, tight-knit single-game communities, LFG servers with matchmaking channels, and dev-followed communities where patch notes hit before Twitter does.",
    tips: [
      "Look for a #lfg or #find-teammates channel with messages in the last hour",
      "Pick multi-region servers if you play late nights — you want mods online 24/7",
      "Skip servers gated behind a level or purchase to talk — the good ones let you in immediately",
      "Voice-channel count matters more than total members for a real gaming server",
    ],
    faq: [
      {
        q: "What's the best Discord server for finding gaming teammates?",
        a: "The top-ranked gaming servers on Serverlistr all have dedicated LFG (looking-for-group) channels — usually split by game. Look for servers with more than 5,000 members and 20+ voice channels; those hit the sweet spot where you'll always find someone online to squad up with.",
      },
      {
        q: "Are gaming Discord servers safe for teens?",
        a: "Discord's minimum age is 13. Most large gaming servers have strict verification, phone-verified requirements, and clear child-safety rules. Look for the 'age-gated' or 'PG-13' tag on any listing before joining — Serverlistr surfaces those clearly on every server page.",
      },
      {
        q: "Why join a game-specific Discord instead of the official one?",
        a: "Official Discord servers tend to be huge, noisy, and heavily moderated. Community-run gaming servers on this list are smaller, faster to make friends in, and usually run their own tournaments, giveaways, and modded servers you won't find on the official one.",
      },
      {
        q: "Do I need to install anything to join a gaming Discord?",
        a: "No. Every server on this list uses a standard Discord invite — click the server name, hit Join, and Discord opens directly. If you don't have Discord yet, the invite will prompt you to create a free account.",
      },
      {
        q: "How often do gaming server rankings change on Serverlistr?",
        a: "Rankings recalculate as Discord member counts sync (multiple times a day) and as owners bump their listings (every 2 hours). A server that just launched a big update or hosted an event will typically jump 5–10 spots overnight.",
      },
    ],
  },

  community: {
    label: "Community Discord Servers",
    noun: "community",
    intro:
      "Community Discord servers are the general-purpose social hubs — the ones you join not for a specific game or hobby, but because the people are the point. This ranked list highlights the 25 largest and most active community servers on Serverlistr: chill hangout servers, friendship-focused communities, active social servers with daily events, and open-topic servers where the conversation ranges from meme spam to career advice in the same channel. Every one is bot-verified and updated in real time.",
    tips: [
      "Read the #welcome channel before joining — it tells you if the vibe is chill or chaotic",
      "Servers with 10+ scheduled events per month are the ones where you'll actually make friends",
      "Watch for staff activity in general chat — the best community servers have visible, human staff",
      "Small (under 2k) community servers usually feel warmer than 50k+ giants",
    ],
    faq: [
      {
        q: "What makes a good community Discord server?",
        a: "The best community servers on this list share three things: active staff who chat like normal members, a healthy events calendar (game nights, movie nights, VC hangouts), and clear rules that get enforced. Size doesn't matter as much as vibe — a 500-member server can feel more like home than a 50,000-member one.",
      },
      {
        q: "How is a community server different from a friend server?",
        a: "Friend servers are private and invite-only. Community servers on Serverlistr are all public, actively growing, and open to anyone who follows the rules. They're built for meeting new people, not just staying in touch with existing friends.",
      },
      {
        q: "Are community Discord servers moderated?",
        a: "Every server on Serverlistr passes bot verification and shows its member count live from Discord, so you can spot inactive or ghost-town servers instantly. Beyond that, individual servers set their own moderation levels — check the #rules or #info channel in the first 30 seconds after joining.",
      },
      {
        q: "What's the difference between a community server and a subreddit?",
        a: "Community Discord servers are real-time — you're chatting live with dozens of people at once, hopping in voice channels, joining events. Subreddits are async and text-based. Both have their place, but if you want to actually hang out with people, Discord wins every time.",
      },
      {
        q: "Can I join more than one community server?",
        a: "Absolutely — most Discord users are in 20+ servers. Community servers work great as your daily-chat home, and you can layer specific-interest servers (gaming, anime, tech) on top.",
      },
    ],
  },

  anime: {
    label: "Anime Discord Servers",
    noun: "anime",
    intro:
      "Anime Discord servers are where fans go to actually talk about what they're watching this season — not scroll takes from strangers, not fight bots on r/anime. This ranked list features the 25 most active anime servers on Serverlistr: broad-scope anime hubs, currently-airing seasonal-anime servers, manga discussion communities, waifu/husbando servers, weeb-culture social servers, and dedicated fandom servers for the big names (Attack on Titan, Jujutsu Kaisen, One Piece, Chainsaw Man, Frieren). Every server is bot-verified with live member counts.",
    tips: [
      "Look for #seasonal-anime or #currently-watching channels — that's where the real chat is",
      "Big anime servers (20k+) often have separate spoiler channels per show — join those",
      "Watch for anime bots like Mudae, Karuta, Sofi — they signal a highly-engaged fandom",
      "Avoid servers with more emoji spam than actual anime chat in general",
    ],
    faq: [
      {
        q: "What's the biggest anime Discord server?",
        a: "Some of the largest anime Discord servers have over 100,000 members. On this ranked list, the #1 server updates in real time based on live Discord counts. The biggest ones cover all-anime-all-the-time; smaller focused servers (single-show or single-genre) are often more fun to actually chat in.",
      },
      {
        q: "Are anime Discord servers age-restricted?",
        a: "It depends on the server. Servers with NSFW/hentai channels are gated behind Discord's 18+ verification (Discord enforces this at the platform level). SFW-only anime servers are open to anyone 13+. Serverlistr tags every listing so you can filter appropriately before joining.",
      },
      {
        q: "Where do people talk about currently-airing anime?",
        a: "Every large anime server on this list has a dedicated 'seasonal' or 'currently-airing' channel with sub-threads per show. That's where real-time reactions land during and right after each episode airs — usually within an hour of the raw release, and again after subs drop.",
      },
      {
        q: "What are anime waifu bots and are they worth it?",
        a: "Bots like Mudae, Karuta, and Sofi let you 'claim' characters from anime and manga as a lightweight collecting mini-game. They're huge in anime servers — if a server tops the rolls channel with 200+ daily rolls, it's a signal the community is highly active and social, not just lurking.",
      },
      {
        q: "Do anime Discord servers spoil what I'm watching?",
        a: "Well-run anime servers on Serverlistr enforce strict spoiler rules — usually through per-show channels and Discord's built-in spoiler tag. Look for a #spoiler-rules pin in the announcements before you dive into general chat.",
      },
    ],
  },

  tech: {
    label: "Tech Discord Servers",
    noun: "tech",
    intro:
      "Tech Discord servers are where builders, tinkerers, and engineers hang out — talking hardware, homelab setups, self-hosting, cybersecurity, networking, AI, startups, and everything in between. This list ranks the top 25 tech communities on Serverlistr, from broad tech-generalist servers to focused niches like /r/homelab-style server admins, GPU-tinker circles, and IT-career support servers. Every server is bot-verified with real member counts pulled live from Discord.",
    tips: [
      "Look for #showoff or #builds channels — a good tech server is a portfolio wall",
      "Career-focused tech servers usually have pinned 'looking for work' and 'hiring' channels",
      "Servers with visible senior engineers answering rookie questions are gold",
      "Skip servers where every third message is a crypto pitch",
    ],
    faq: [
      {
        q: "What's the best tech Discord server for beginners?",
        a: "Beginner-friendly tech servers on this list share one trait: a pinned 'no dumb questions' or #help-me channel with active senior members answering within minutes. Look for servers under 20k members — the giants are noisy and beginner questions get buried.",
      },
      {
        q: "Are tech Discord servers useful for career advice?",
        a: "Yes — the career-focused tech servers on Serverlistr are one of the most efficient sources of practical career advice you'll find online. Resume feedback, interview prep, salary negotiation, and 'is this offer good?' threads happen daily. Many servers also have members-only job boards.",
      },
      {
        q: "How is a tech Discord different from Stack Overflow or Reddit?",
        a: "Stack Overflow is for well-formed questions with permanent answers. Tech Discord servers are for real-time conversations — debugging with someone in voice chat, workshopping architecture decisions, or venting about your on-call rotation. It's collaborative in a way async platforms aren't.",
      },
      {
        q: "Do tech Discord servers cover AI and ML?",
        a: "Absolutely. AI/ML-focused tech servers have exploded in the last few years — many of the top-ranked tech servers on Serverlistr are dedicated to LLMs, prompt engineering, model training, or building agent frameworks. They're where practitioners share prompt tricks weeks before the blog posts appear.",
      },
      {
        q: "What's a homelab Discord and should I join one?",
        a: "Homelab servers are for people running self-hosted infrastructure — Proxmox clusters, Kubernetes at home, Plex/Jellyfin stacks, security cameras, home automation. If you own a mini-PC or old server humming in a closet, a homelab Discord will teach you more in a week than a semester of certs.",
      },
    ],
  },

  programming: {
    label: "Programming Discord Servers",
    noun: "programming",
    intro:
      "Programming Discord servers are where developers actually get unstuck — where a Python question gets answered in five minutes and a system-design debate can run all night. This ranked list features the top 25 programming communities on Serverlistr: language-specific servers (Python, JavaScript, Rust, Go, C++), framework hubs (React, Next.js, Django, Rails), general-CS learning servers, and pro-dev communities where senior engineers hang out. Every server is bot-verified.",
    tips: [
      "Look for a #help-forum channel that uses Discord's Forum Channels — signals a mature server",
      "Big language servers usually have per-topic channels (data-viz, web, systems) — join those",
      "The best programming servers reward good questions with pins, not with insults",
      "Junior-dev-friendly servers are usually language-first, not framework-first",
    ],
    faq: [
      {
        q: "What's the best programming Discord for learning to code?",
        a: "Beginner-friendly programming servers on this list all use Discord's Forum Channels for questions so nothing gets lost in scroll. The best ones have a #daily-practice channel with new coding challenges every 24 hours — great for building the habit without the pressure of LeetCode.",
      },
      {
        q: "Are programming Discord servers language-specific or general?",
        a: "Both. This list has both — big general programming servers (thousands of members across every language) and focused single-language servers (Python-only, JS-only, Rust-only). Most working developers join one general server for the vibe and one language server for the deep questions.",
      },
      {
        q: "Can I get code review in a programming Discord?",
        a: "Yes — most of the top-ranked programming servers on Serverlistr have a #code-review or #review-my-project channel. Post a link to your GitHub repo or paste a code block; you'll usually get feedback within a few hours. Some servers even do weekly voice-chat 'review nights' where senior devs walk through submitted code live.",
      },
      {
        q: "How do programming Discord servers compare to Stack Overflow?",
        a: "Stack Overflow is a permanent Q&A archive; Discord is real-time help. Discord's advantage: you can debug interactively with someone, share screenshots, jump into voice chat, or just ask a dumb question without getting closed for being 'too broad'. Different tools for different problems.",
      },
      {
        q: "Are there Discord servers for open-source contributors?",
        a: "Many top programming servers host active open-source contributor communities — usually with pinned 'good first issues' from major projects. If you want to break into open source, joining one of these servers is often faster than trawling GitHub cold.",
      },
    ],
  },

  music: {
    label: "Music Discord Servers",
    noun: "music",
    intro:
      "Music Discord servers are for people who take listening seriously — audiophiles arguing about DACs, hip-hop heads dissecting new drops, producers sharing WIPs, K-pop stans running album parties, and vinyl collectors flexing their crates. This list ranks the top 25 music servers on Serverlistr: genre-specific communities, producer/beatmaker servers, listening-party servers, and general music-lover hangouts. Every server is bot-verified with live member counts.",
    tips: [
      "Producer servers with active #wip and #feedback channels beat the marketing-only ones",
      "K-pop and hip-hop stan servers usually spike during album releases — join before the drop",
      "Music-listening servers with Groovy/Rythm-style bots are still going strong on this list",
      "Genre-specific servers usually have more focused conversation than general-music hubs",
    ],
    faq: [
      {
        q: "Are there Discord servers where people share new music?",
        a: "Yes — the top music servers on this list all have #new-releases or #recommendations channels flooded with links every hour. If you're tired of algorithmic playlists, this is the fastest way to hear new music from actual humans with taste.",
      },
      {
        q: "Can I share my own music in a music Discord server?",
        a: "Most producer-focused servers on Serverlistr have dedicated #self-promo or #share-your-work channels. General music servers are stricter — they'll usually only allow promo if you're an active regular first. Read the rules pinned in #welcome before posting.",
      },
      {
        q: "What's a listening party on Discord?",
        a: "A listening party is a synchronized event — everyone joins the same voice channel and a bot plays a full album at the same time, so you can react and chat live. Big K-pop, hip-hop, and indie servers on this list run these weekly, and they're some of the most fun events on Discord.",
      },
      {
        q: "Do music Discord servers have music bots?",
        a: "Many still do. After the 2021 crackdown on YouTube-scraping bots, most servers moved to Spotify-integrated or self-hosted bots. Check a server's #bots channel — if it's active, you'll find something playing 24/7.",
      },
      {
        q: "What's the difference between a genre server and a fan server?",
        a: "Genre servers (hip-hop, indie, K-pop, rock) cover everything in the genre. Fan servers focus on one artist or band. Fan servers spike hard around releases; genre servers are steady, everyday-vibe places.",
      },
    ],
  },

  art: {
    label: "Art & Design Discord Servers",
    noun: "art & design",
    intro:
      "Art and design Discord servers are where creators actually get feedback — not vague 'looks good!' comments but real critique from other artists working the same craft. This list ranks the top 25 art servers on Serverlistr: digital art communities, traditional-media servers, UI/UX design communities, character-design and illustration servers, animation servers, and pro-portfolio critique communities. Every server is bot-verified.",
    tips: [
      "Servers with weekly art challenges beat servers with just #share-your-work channels",
      "Look for #wip channels — artists who post progress get better crit than one-shot posters",
      "Design (UI/UX) servers are usually more career-focused than illustration servers",
      "Avoid AI-only art servers if you want real human-craft feedback",
    ],
    faq: [
      {
        q: "Where can I get honest feedback on my art in a Discord server?",
        a: "The best art servers on this list have dedicated #critique or #review channels with clear posting rules — usually 'post a WIP, tell us what you want feedback on, no low-effort responses'. Small-to-mid art servers (2k–15k) give more useful crit than mega-servers where posts scroll off in minutes.",
      },
      {
        q: "Are art Discord servers good for finding clients?",
        a: "Some are — especially UI/UX and illustration servers with #hiring or #commissions channels. Design-career servers often post real client leads. Avoid servers where every second message is spam-DMing artists for 'exposure work'; the good ones actively ban that.",
      },
      {
        q: "What's the difference between an illustration server and a general art server?",
        a: "Illustration servers focus on drawing-based work (character design, concept art, comics, digital paint). General art servers cover everything from traditional oil painting to 3D sculpting to graphic design. If your discipline is niche, a focused server usually beats a giant general one.",
      },
      {
        q: "Do art Discord servers welcome beginners?",
        a: "Most on this list explicitly do — look for #beginners or #newbie-friendly channels. Beginner-friendly servers pair rookies with feedback from mid-level artists, and often run 'draw this again in a year' progress threads that are genuinely inspiring.",
      },
      {
        q: "Are AI art servers different from traditional art servers?",
        a: "Increasingly, yes. Many traditional art servers on Serverlistr explicitly ban AI-generated posts in #share-your-work; AI-first servers welcome them. If you're using tools like Midjourney or Stable Diffusion, look for a dedicated AI-art server rather than posting in a mixed-media community.",
      },
    ],
  },

  education: {
    label: "Education Discord Servers",
    noun: "education",
    intro:
      "Education Discord servers are quiet study hubs and loud debate rooms — places where students hop into voice channels to study together, teachers share resources, and self-taught learners map their way through free curricula. This list ranks the top 25 education servers on Serverlistr: study-together servers with Pomodoro bots, subject-specific tutoring servers, exam-prep communities (SAT, MCAT, LSAT, AP), and language-learning servers. Every server is bot-verified.",
    tips: [
      "Study-together servers with 24/7 voice channels are the ones that actually help you focus",
      "Look for #resources channels with pinned free courses and PDF collections",
      "Exam-prep servers spike in traffic 3 months before test dates — join early",
      "Language-learning servers with native-speaker mentors are more valuable than app-based tutoring",
    ],
    faq: [
      {
        q: "How does a study-together Discord server work?",
        a: "Study-together servers use silent voice channels — you join a VC with 5–20 other people, cameras and mics off, everyone works quietly on their own tasks. It's virtual coworking. Most on this list have Pomodoro bots that cycle 25-minute work + 5-minute break rounds automatically.",
      },
      {
        q: "Are there Discord servers for specific exams like SAT or MCAT?",
        a: "Yes — several top-ranked education servers on Serverlistr are dedicated to standardized-test prep. You'll find shared study materials, weekly practice-test sessions, and members who've recently taken the exam sharing what actually mattered on test day.",
      },
      {
        q: "Can I find tutors in an education Discord server?",
        a: "Most tutoring on these servers is peer-to-peer and free — students helping students. Some servers have vetted paid-tutor lists in pinned channels, but the culture is 'ask in general, get help in five minutes' for the majority of subjects.",
      },
      {
        q: "Are language-learning Discord servers effective?",
        a: "The good ones are — because they give you what apps like Duolingo can't: real conversation with native speakers. The top language servers on this list have daily voice-chat 'language exchange' hours where beginners pair with fluent speakers of their target language.",
      },
      {
        q: "Do teachers use Discord for education?",
        a: "Some do — especially for extracurricular clubs, tutoring, and after-hours help. Discord's not typically used as an official classroom platform (schools use Google Classroom / Canvas for that), but plenty of the education servers here are teacher-run and student-friendly.",
      },
    ],
  },

  books: {
    label: "Books & Writing Discord Servers",
    noun: "books & writing",
    intro:
      "Books and writing Discord servers are where readers who devour 60 books a year finally find their people — and where writers stop writing alone. This list ranks the top 25 books-and-writing servers on Serverlistr: reading-focused communities, genre-specific book clubs (fantasy, romance, sci-fi, literary fiction), writing critique servers, NaNoWriMo-year-round groups, poetry servers, and self-publishing communities. Every server is bot-verified.",
    tips: [
      "Book clubs with rotating monthly reads are more active than 'read whatever' servers",
      "Writing servers with weekly word-count challenges keep you accountable",
      "Genre-specific servers give better recommendations than broad 'books' servers",
      "Self-publishing servers are often more useful than general writing servers for actual income",
    ],
    faq: [
      {
        q: "How does a Discord book club work?",
        a: "Most book club servers on this list pick a monthly read (voted on by members), post reading milestones through the month, then run a live voice discussion at the end. Some also have permanent per-book channels so you can lurk older discussions of books you've already read.",
      },
      {
        q: "Can I get my writing critiqued in a Discord server?",
        a: "Yes — writing servers on Serverlistr all have dedicated critique channels with posting rules (word count limits, tag requirements, reciprocal-feedback expectations). The best give you slow, thoughtful line-edits rather than fast 'looks good' comments.",
      },
      {
        q: "Are Discord servers good for NaNoWriMo?",
        a: "Many of the top-ranked writing servers on this list run year-round NaNoWriMo-style challenges — daily word sprints, live writing sessions in voice channels, and shared word-count leaderboards. If you write better with others in the room, these servers are the equivalent of a virtual writing café.",
      },
      {
        q: "Where do self-published authors hang out on Discord?",
        a: "Several servers on this list focus specifically on indie/self-publishing — cover design, KDP strategy, ad targeting, launching a series, newsletter growth. These are much more useful than generic writing servers if your goal is actually selling books.",
      },
      {
        q: "Are there Discord servers for specific book genres?",
        a: "Yes — big genre servers exist for fantasy, romance, sci-fi, horror, romantasy, LitRPG, and more. Genre-focused servers make it easy to find recommendations matched to your taste rather than reading whatever the algorithm serves you.",
      },
    ],
  },

  roleplay: {
    label: "Roleplay Discord Servers",
    noun: "roleplay",
    intro:
      "Roleplay Discord servers are where writers-who-happen-to-be-collaborators build worlds together — turn-based text roleplays, live voice sessions, D&D campaigns, freeform world-building, and fandom-based RP set in every universe imaginable. This list ranks the top 25 roleplay servers on Serverlistr: freeform RP servers, fandom RP servers, tabletop-RPG servers, one-on-one RP hubs, and long-running campaign servers. Every server is bot-verified.",
    tips: [
      "Look for a #character-approval or #applications channel — signals a well-moderated RP",
      "Long-running RP servers (2+ years active) usually have deeper lore and better writers",
      "Fandom RP servers are more accessible than original-world servers for first-timers",
      "Servers with Tupperbox or PluralKit-style bots make RP feel much more polished",
    ],
    faq: [
      {
        q: "How do I start roleplaying on Discord?",
        a: "Pick a server matched to your style — freeform, fandom, or tabletop. Read the lore/world doc, apply your character in the #applications channel, wait for approval, then dive in. The good servers on this list have onboarding channels specifically designed for new members.",
      },
      {
        q: "What's the difference between freeform and fandom roleplay?",
        a: "Freeform RP servers build original worlds from scratch — you're a co-writer. Fandom RP is set in existing universes (Harry Potter, Marvel, Naruto, HxH, Genshin, etc.), so worldbuilding is done and you can jump straight into character. Fandom is easier for beginners; freeform is deeper for veterans.",
      },
      {
        q: "Are D&D and TTRPG servers different from regular roleplay servers?",
        a: "Yes — tabletop servers focus on structured game-mechanics play (dice rolls, character sheets, DMs running sessions). Regular RP servers are more free-flowing prose writing. Both are represented in this list; make sure you know which style you're joining before you apply.",
      },
      {
        q: "Are there age-restricted RP servers?",
        a: "Many roleplay servers on Serverlistr are 18+ only — for either mature themes or NSFW content. These are gated behind Discord's platform-level age verification. Every server on this list is tagged clearly so you can filter to age-appropriate options.",
      },
      {
        q: "What's Tupperbox and why do RP servers use it?",
        a: "Tupperbox is a bot that lets you post as different characters with unique avatars and names — so a single Discord account can voice multiple characters cleanly in a roleplay. Most polished RP servers on this list use Tupperbox or PluralKit; it's the fastest way to spot a mature RP community.",
      },
    ],
  },

  crypto: {
    label: "Crypto Discord Servers",
    noun: "crypto",
    intro:
      "Crypto Discord servers are where alpha lives — new projects, on-chain analysis, DeFi strategy, NFT drops, and (yes) the good and bad memes. This list ranks the top 25 crypto communities on Serverlistr: general crypto-trading servers, specific-project communities, NFT-focused servers, DeFi-strategy servers, and Web3 developer hubs. Every server is bot-verified with live member counts.",
    tips: [
      "Servers with active on-chain-analysis channels beat pure-hype servers every time",
      "Beware any server DMing you first — real crypto servers never cold-DM offers",
      "Developer-focused Web3 servers (Solidity, Rust, zkSync) are usually higher-signal than trading servers",
      "Look for a #verify or #humans-only channel — those servers have working anti-bot systems",
    ],
    faq: [
      {
        q: "Are crypto Discord servers safe from scams?",
        a: "The reputable ones enforce strict scam prevention — verification bots, first-message delays, and clear 'staff will never DM you first' pins. That said, always assume anyone DMing you a crypto opportunity is a scammer, no matter which server you're in. Real servers exist; the risk is real too.",
      },
      {
        q: "What's the difference between a project Discord and a general crypto Discord?",
        a: "Project Discords (like a specific L2 chain or DeFi protocol) are official community hubs — announcements, support, governance discussion. General crypto Discords are cross-project trading, alpha-sharing, and market-analysis communities. Most active traders are in multiple of both.",
      },
      {
        q: "Do NFT Discord servers still matter after the 2022 crash?",
        a: "The serious ones do — they've matured into art-focused and utility-focused communities rather than flip-driven mania. On this list, the top-ranked NFT servers are the ones with active generative-art discussion, on-chain gaming, or genuine collector culture rather than pure floor-price chatter.",
      },
      {
        q: "Are there Discord servers for crypto beginners?",
        a: "Yes — several servers on Serverlistr specifically target new users, with #starter-here channels, weekly 'basics' AMAs, and no-question-too-dumb culture. Look for tags like 'beginner-friendly' or 'no-shill'.",
      },
      {
        q: "Which crypto Discord servers have real developer activity?",
        a: "Solidity, Move, Rust-on-Solana, and zkVM developer servers are among the most active on this list — genuine builders shipping code, reviewing each other's contracts, and posting testnet demos. These are dramatically different in vibe from trading-focused servers.",
      },
    ],
  },

  esports: {
    label: "Esports Discord Servers",
    noun: "esports",
    intro:
      "Esports Discord servers are where competitive players and fans of pro play converge — team fan servers, tournament-organizer communities, scrim-partner-finding servers, and pro-scene analysis hubs. This list ranks the top 25 esports servers on Serverlistr: League of Legends, CS2, Valorant, Rocket League, Overwatch, Dota 2, and multi-game esports servers. Every server is bot-verified.",
    tips: [
      "Team fan servers spike during tournaments — some hit 100k+ members mid-Worlds",
      "Scrim-finding servers usually list team ranks openly — pick one at your level",
      "Analysis-heavy servers with pinned VOD reviews beat pure-hype fan servers",
      "Regional esports servers (NA/EU/KR/BR) tend to be more organized than global ones",
    ],
    faq: [
      {
        q: "How do I find scrim partners for my esports team?",
        a: "Every major game has dedicated scrim-finding Discord servers on this list — usually organized by game, region, and rank tier. Post your team's ranks, region, and available times in the appropriate channel; you'll usually find opponents within a few hours.",
      },
      {
        q: "Are there Discord servers for specific pro teams?",
        a: "Yes — most major pro esports orgs have official fan Discord servers, and unofficial fan servers exist for basically every recognizable team. On this list, you'll find both categories.",
      },
      {
        q: "Do esports Discord servers host their own tournaments?",
        a: "Many do. Community tournament organizers use Discord as their operational hub — announcements, brackets, VC lobbies for match check-ins, streamer coordination. The best-run TO servers on Serverlistr host tournaments every weekend.",
      },
      {
        q: "What's the difference between an esports server and a regular gaming server?",
        a: "Regular gaming servers cover casual play, LFG, and everything from co-op to solo. Esports servers focus on competitive play — scrims, tournament results, meta analysis, and pro-scene discussion. Different vibes; the same person often joins both.",
      },
      {
        q: "Can I break into pro esports through Discord?",
        a: "Discord is where amateur competitive scenes exist — semi-pro leagues, scrim networks, and grass-roots tournaments. Almost every pro player on the current pro scene came up through community Discord servers before signing with an org.",
      },
    ],
  },

  memes: {
    label: "Meme Discord Servers",
    noun: "meme",
    intro:
      "Meme Discord servers are the closest thing the internet still has to shared culture — the places where fresh memes drop before Twitter and where inside jokes are minted in real time. This list ranks the top 25 meme servers on Serverlistr: general meme hangouts, gaming-meme servers, anime-meme communities, and shitposting servers with distinct cultures of their own. Every server is bot-verified.",
    tips: [
      "OC-only (original content) meme servers are usually higher-quality than repost servers",
      "Weekly meme contests are a great signal of an engaged, active community",
      "Skip meme servers with no rules — they're 99% NSFW spam or dead",
      "Meme servers with 20k+ members and active general chat are hard to find; join those first",
    ],
    faq: [
      {
        q: "What makes a good meme Discord server?",
        a: "Active daily posting in the #memes or #shitpost channel, clear anti-repost rules, and a general channel where people actually chat (not just react-only). The best meme servers on this list feel more like a friend group than a content firehose.",
      },
      {
        q: "Are meme Discord servers age-restricted?",
        a: "Some are — servers with adult humor or NSFW meme content are 18+ gated. SFW-only meme servers welcome anyone 13+ (Discord's minimum age). Every server on Serverlistr is tagged so you can filter appropriately.",
      },
      {
        q: "What's an OC meme server?",
        a: "OC (original content) meme servers require every posted meme to be made by the poster — not reposted from Twitter, Reddit, or other Discords. These servers reward creativity over recycling, and the running jokes get genuinely funny.",
      },
      {
        q: "Do meme servers do weekly contests?",
        a: "Many of the top-ranked ones do — usually a themed prompt every Sunday, community voting Wednesday, winners announced Friday. Some have Nitro or steam-game prizes; others just clout. It's a great way to level up your meme game.",
      },
      {
        q: "Can I share memes across multiple servers?",
        a: "Sure — most people do. Just watch OC-only server rules; if you make an original meme in one server and repost to another, you're usually fine. Copying other people's memes and calling them your own is where you'll get banned.",
      },
    ],
  },
};

export const ALL_CATEGORY_IDS = Object.keys(CATEGORY_META);

// Overall (non-category) landing page copy — hand-written so it doesn't share
// language with any category page.
export const OVERALL_META = {
  intro:
    "Looking for the best Discord servers to join in 2026? This ranked list surfaces the top 25 Discord communities on Serverlistr right now — verified by our bot, weighted by live member counts, and refreshed every time an owner bumps their listing. Whether you're hunting for gaming squads, anime discussion, a chill community to hang out in, a study group, a crypto alpha channel, or a writing critique circle, you'll find something worth joining below. Every server is checked against Discord in real time — no dead invites, no fake counts.",
  tips: [
    "Bumped-in-the-last-24-hours servers are almost always active — use it as a fast filter",
    "Servers with 5+ 5-star reviews on Serverlistr are typically well-moderated",
    "Look at the tags on each listing — 'active-voice-chat' and 'daily-events' are strong signals",
    "Join a general community server as your daily-chat home, then add topic-specific servers on top",
  ],
  faq: [
    {
      q: "How do I join a Discord server from this list?",
      a: "Click the server name to open its full listing on Serverlistr, then hit Join. The invite opens Discord directly — no account or download required beyond a free Discord account.",
    },
    {
      q: "Are these Discord servers safe?",
      a: "Every server is verified via our Discord bot before it can appear here, and dead or spam servers are automatically de-listed. If you spot rule-breaking content, hit Report on any listing — real humans review every report.",
    },
    {
      q: "How often does this best-of ranking update?",
      a: "Continuously. Member counts sync from Discord multiple times a day; owners can bump their listings every 2 hours to move up the rankings; and rankings recalculate live off of both signals.",
    },
    {
      q: "Can I list my own Discord server on Serverlistr?",
      a: "Yes — Serverlistr is free to list on. Sign in with Discord, add our bot to your server for verification, submit your listing, and you'll appear in the appropriate best-of lists automatically once approved.",
    },
    {
      q: "What makes Serverlistr different from other Discord server lists?",
      a: "Every listing is bot-verified — no fake counts, no dead invites. We also rank by live activity (bumps, member changes, reviews) rather than paid placement alone. Featured spots exist for owners who want extra visibility, but the main rankings are earned.",
    },
    {
      q: "Do I need a Discord account to browse this list?",
      a: "No — you can browse Serverlistr, read reviews, and explore every listing without logging in. You only need a Discord account to actually join a server or to list one of your own.",
    },
  ],
};

export function prettyCategory(id) {
  return CATEGORY_META[id]?.label || id;
}

// --- Programmatic seed composition --------------------------------
// Given a seed (see bestListSeeds.js), compose a unique intro, tips, and
// FAQ. The composition rules vary by seed `type` so game pages read like
// game pages, country pages read like country pages, etc. — the shared
// pool is the only thing with templated structure. Everything else is
// substituted from the seed's `facts` and `hooks` arrays.

const INTRO_TEMPLATES = {
  game: (s) =>
    `Looking for the best ${s.display} Discord servers? ${s.display} is ${s.facts[0]}, ${s.facts[1] ? "and " + s.facts[1] : ""}. This ranked list surfaces the top 25 ${s.display} communities on Serverlistr right now — every one verified by our Discord bot, weighted by live member counts and recent activity. Whether you're hunting for ${s.hooks[0] || "squadmates"}, ${s.hooks[1] || "strategy discussion"}, or ${s.hooks[2] || "a chill community to grind alongside"}, you'll find real players below. ${s.facts[2] ? "Context: " + s.facts[2] + "." : ""}`,
  anime: (s) =>
    `Looking for the best ${s.display} Discord servers? ${s.display} is ${s.facts[0]}. ${s.facts[1] ? s.facts[1][0].toUpperCase() + s.facts[1].slice(1) + "." : ""} This ranked list features the top 25 ${s.display} fan communities on Serverlistr — every server bot-verified with live member counts. Expect ${s.hooks[0] || "spoiler-safe discussion channels"}, ${s.hooks[1] || "fan-art showcase rooms"}, and ${s.hooks[2] || "theory-crafting threads"} that keep conversation fresh between episodes and chapters. ${s.facts[2] ? s.facts[2][0].toUpperCase() + s.facts[2].slice(1) + "." : ""}`,
  country: (s) =>
    `Looking for the best ${s.display} Discord servers? ${s.facts[0][0].toUpperCase() + s.facts[0].slice(1)}. This ranked list features the top 25 ${s.display} communities on Serverlistr — every server bot-verified with live member counts. Members cluster around ${s.hooks[0] || "regional voice-chat channels"} and ${s.hooks[1] || "country-specific sub-rooms"}, giving you active chat in your timezone instead of ghost-town servers five hours out of sync. ${s.facts[1] ? s.facts[1][0].toUpperCase() + s.facts[1].slice(1) + "." : ""} ${s.facts[2] ? s.facts[2][0].toUpperCase() + s.facts[2].slice(1) + "." : ""}`,
  interest: (s) =>
    `Looking for the best ${s.display} Discord servers? ${s.facts[0][0].toUpperCase() + s.facts[0].slice(1)}. This ranked list features the top 25 ${s.display} communities on Serverlistr right now — every server bot-verified, live member counts updated continuously. You'll find ${s.hooks[0] || "focused discussion channels"}, ${s.hooks[1] || "resource-sharing threads"}, and ${s.hooks[2] || "peer-support rooms"} across the communities below. ${s.facts[1] ? s.facts[1][0].toUpperCase() + s.facts[1].slice(1) + "." : ""} ${s.facts[2] ? s.facts[2][0].toUpperCase() + s.facts[2].slice(1) + "." : ""}`,
  vibe: (s) =>
    `Looking for ${s.display.toLowerCase()} Discord servers? ${s.facts[0][0].toUpperCase() + s.facts[0].slice(1)}. This ranked list surfaces the top 25 ${s.display.toLowerCase()} servers on Serverlistr — every one bot-verified so you're not landing on a dead invite. Members gravitate here for ${s.hooks[0] || "a specific vibe"}, ${s.hooks[1] || "a particular culture"}, and ${s.hooks[2] || "a different scale than mega-servers"}. ${s.facts[1] ? s.facts[1][0].toUpperCase() + s.facts[1].slice(1) + "." : ""} ${s.facts[2] ? s.facts[2][0].toUpperCase() + s.facts[2].slice(1) + "." : ""}`,
};

const TIPS_BUILDERS = {
  game: (s) => [
    `Look for ${s.display}-specific LFG channels (not just a #general) — that's where you'll actually find teammates`,
    `${s.hooks[0] ? "Join a server with " + s.hooks[0] + "; the serious players congregate there" : "Pick servers where members talk about recent patches, not just skins"}`,
    `${s.hooks[1] ? "Look for " + s.hooks[1] + " — signals the community actually plays, not just lurks" : "Multi-region servers are better if your schedule varies"}`,
    `Avoid servers with 50k+ members but a dead #${s.slug} channel — those are usually crosslisted bots`,
  ],
  anime: (s) => [
    `Look for ${s.display} servers with per-arc spoiler channels — signals mature moderation`,
    `${s.hooks[0] ? s.hooks[0][0].toUpperCase() + s.hooks[0].slice(1) + " are a strong signal of engaged members" : "Active-episode discussion within an hour of release is the gold standard"}`,
    `Avoid ${s.display} servers that are 90% emoji spam and 10% actual chat`,
    `${s.hooks[2] ? "Servers with " + s.hooks[2] + " attract the dedicated-fandom crowd rather than casual lurkers" : "Smaller focused servers often beat 50k+ mega-fandoms for actual conversation"}`,
  ],
  country: (s) => [
    `Join ${s.display} servers that match your actual timezone — a dead chat is worse than no chat`,
    `${s.hooks[0] ? "Look for " + s.hooks[0] + " to filter by language preference" : "Regional sub-channels help you find people near you"}`,
    `${s.hooks[1] ? s.hooks[1][0].toUpperCase() + s.hooks[1].slice(1) + " is a feature of well-structured regional servers" : "Country-specific gaming LFG rooms let you play with low-ping teammates"}`,
    `Avoid "international" servers that claim to be ${s.display}-focused but post 90% English — those are usually gated farming operations`,
  ],
  interest: (s) => [
    `Look for ${s.display} servers with forum-style help channels — that's a sign of mature moderation`,
    `${s.hooks[0] ? s.hooks[0][0].toUpperCase() + s.hooks[0].slice(1) + " show the community is actively engaged, not just lurking" : "Weekly scheduled events are a good signal of a living community"}`,
    `${s.hooks[1] ? "Beginner-friendly servers highlight " + s.hooks[1] + " so new members land softly" : "Avoid servers where senior members gatekeep beginner questions"}`,
    `Small-to-mid-size ${s.display} servers often give better value than mega-servers with buried questions`,
  ],
  vibe: (s) => [
    `${s.display}-focused servers filter by culture, not topic — read the welcome channel to confirm vibe before joining`,
    `${s.hooks[0] ? "Look for " + s.hooks[0] + " as a signal the vibe is real, not aspirational" : "Vibe servers thrive on recognizable regulars, not endless new-member churn"}`,
    `${s.hooks[1] ? s.hooks[1][0].toUpperCase() + s.hooks[1].slice(1) + " are the markers of a well-cultured community" : "Clear and enforced rules matter more here than in topic-focused servers"}`,
    `Avoid servers that market a vibe but don't deliver — if general chat contradicts the pitch within 10 minutes, bail`,
  ],
};

const FAQ_BUILDERS = {
  game: (s) => [
    {
      q: `What's the best Discord server for playing ${s.display}?`,
      a: `The top-ranked ${s.display} server on this list changes based on live Discord member counts and recent bump activity. The best option for YOU depends on what you want — ${s.hooks[0] || "casual pickup groups"}, ${s.hooks[1] || "ranked team-ups"}, or ${s.hooks[2] || "pure community chat"}.`,
    },
    {
      q: `How do I find ${s.display} teammates on Discord?`,
      a: `Join any top-ranked ${s.display} server on Serverlistr and head to its LFG (looking-for-group) channel. Post your rank/region/available times — you'll usually find squadmates within minutes on active servers. Avoid random DMs; stick to the server's matchmaking channels.`,
    },
    {
      q: `Are ${s.display} Discord servers free to join?`,
      a: `Yes — every ${s.display} server on this list is a free Discord server. Click the server name, hit Join, and the invite opens Discord directly. You only need a free Discord account.`,
    },
    {
      q: `How often do ${s.display} server rankings update?`,
      a: `Rankings refresh continuously. Live Discord member counts sync multiple times a day, and owners can bump their listings every 2 hours to move up — so a server that just launched a tournament or released a video can jump 10+ spots overnight.`,
    },
    {
      q: `Can I list my own ${s.display} Discord server?`,
      a: `Yes — Serverlistr is free to list on. Sign in with Discord, add our bot for verification, submit your listing, and once approved it'll appear on this page and the main ${s.display} search results.`,
    },
  ],
  anime: (s) => [
    {
      q: `Where do fans discuss new ${s.display} chapters or episodes?`,
      a: `Every top-ranked ${s.display} server on this list has a dedicated discussion channel — usually with sub-threads for each arc or episode. Spoiler channels are gated so you can read along safely. Reactions typically land within an hour of a new release.`,
    },
    {
      q: `Are ${s.display} Discord servers safe from spoilers?`,
      a: `Well-run ${s.display} servers on Serverlistr enforce strict spoiler rules — usually through per-arc channels and Discord's built-in spoiler tag. Check the pinned #spoiler-rules in announcements before diving into general chat.`,
    },
    {
      q: `Can I share fan-art or AMVs in ${s.display} servers?`,
      a: `Most ${s.display} servers have dedicated #fan-art, #AMV, or #showcase channels for creator content. Server rules vary — some require OC-only, others allow curated reposts with credit. Read the pins before posting.`,
    },
    {
      q: `What's the biggest ${s.display} Discord server?`,
      a: `The #1 server on this ranked list is the current biggest ${s.display} server on Serverlistr by live member count. Big servers are great for breadth; smaller focused servers often give you better per-message conversation.`,
    },
    {
      q: `Are ${s.display} Discord servers age-restricted?`,
      a: `Discord requires users to be 13+. Any ${s.display} servers with NSFW or mature-content channels are 18+ gated via Discord's platform-level verification. Serverlistr tags each listing so you can filter appropriately.`,
    },
  ],
  country: (s) => [
    {
      q: `What are the best ${s.display} Discord servers right now?`,
      a: `The top-ranked ${s.display} servers on this page are the biggest and most actively bumped on Serverlistr. Rankings update continuously based on live member counts and recent activity, so this list stays current.`,
    },
    {
      q: `Are these ${s.display} servers only in that language?`,
      a: `Most primary-language channels are in ${s.display}, but many servers also run English language-exchange channels and welcome learners. Server tags on each listing will clarify the primary language and any sub-communities.`,
    },
    {
      q: `What timezone do ${s.display} Discord servers operate on?`,
      a: `${s.facts[2] || "Most servers cluster around the region's primary timezone"}. Peak activity usually falls between 18:00 and 00:00 local time. Pick servers that match YOUR actual availability — a dead chat during your evening defeats the purpose.`,
    },
    {
      q: `Can I join ${s.display} servers if I don't speak the language fluently?`,
      a: `Most ${s.display} communities welcome learners — many run dedicated language-exchange channels where native speakers help beginners in exchange for English (or other target language) practice. It's one of the most practical ways to use Discord for language learning.`,
    },
  ],
  interest: (s) => [
    {
      q: `What's the best Discord server for ${s.display.toLowerCase()}?`,
      a: `The top-ranked ${s.display} server on this list reflects live member counts and recent activity. The "best" one depends on what you want — ${s.hooks[0] || "focused help channels"}, ${s.hooks[1] || "resource-sharing threads"}, or ${s.hooks[2] || "a social community around the topic"}.`,
    },
    {
      q: `Are ${s.display} Discord servers beginner-friendly?`,
      a: `Many top-ranked ${s.display} servers on Serverlistr have dedicated beginner or #help-forum channels. Look for servers with pinned "new here? start here" messages — those are the ones that onboard newcomers well. Avoid ones where general chat is dominated by veterans gate-keeping.`,
    },
    {
      q: `Can I get help with ${s.display.toLowerCase()} in a Discord server?`,
      a: `Yes — most of the top ${s.display} servers on this list run active help channels where community members answer questions within minutes. For complex problems, use Discord Forum Channels when the server offers them — your question stays searchable instead of scrolling off.`,
    },
    {
      q: `How is a ${s.display} Discord server different from a subreddit?`,
      a: `Discord is real-time and conversational — you can hop into voice chat, debug collaboratively, or just chat live. Reddit is async and better for permanent reference content. Most people use both for different purposes within the same interest.`,
    },
    {
      q: `How do I get my ${s.display} Discord server listed?`,
      a: `Sign in with Discord on Serverlistr, add our verification bot to your server, submit your listing, and once approved it'll appear here. Make sure to tag it with "${s.slug}" or similar relevant tags so it appears on this page.`,
    },
  ],
  vibe: (s) => [
    {
      q: `What makes a Discord server feel ${s.display.toLowerCase()}?`,
      a: `${s.facts[0][0].toUpperCase() + s.facts[0].slice(1)}. ${s.display.toLowerCase()} servers filter not by topic but by culture — moderation style, member tone, and the activity rhythm all contribute. Look at a server's #general for 60 seconds before joining — the vibe reveals itself fast.`,
    },
    {
      q: `Are ${s.display.toLowerCase()} Discord servers better than big active ones?`,
      a: `Different. ${s.display.toLowerCase()} servers trade raw member count for a specific feel. If you've been in massive servers and feel invisible, a ${s.display.toLowerCase()} server will give you something those can't deliver.`,
    },
    {
      q: `How do I find a ${s.display.toLowerCase()} Discord server?`,
      a: `This page lists them, ranked. Filter further by category or topic combined with "${s.slug}" to find the exact niche + vibe match. Serverlistr tags each listing so you can combine filters.`,
    },
    {
      q: `Can I report a server that misrepresents its vibe?`,
      a: `Yes — every Serverlistr listing has a Report button. Flagged listings are reviewed by real humans, and servers that consistently misrepresent their category or vibe are de-listed.`,
    },
  ],
};

/** Compose a full parsed-slug payload from a seed (same shape parseSlug returns). */
export function composeSeedPage(seed) {
  const year = new Date().getFullYear();
  const introFn = INTRO_TEMPLATES[seed.type] || INTRO_TEMPLATES.interest;
  const tipsFn = TIPS_BUILDERS[seed.type] || TIPS_BUILDERS.interest;
  const faqFn = FAQ_BUILDERS[seed.type] || FAQ_BUILDERS.interest;
  return {
    seed,
    tag: seed.slug, // used for the servers filter
    title: `Best ${seed.display} Discord Servers to Join in ${year}`,
    h1: `The Best ${seed.display} Discord Servers`,
    intro: introFn(seed).replace(/\s+/g, " ").trim(),
    tips: tipsFn(seed),
    faq: faqFn(seed),
  };
}
